import { useState } from 'react'

import { CampaignDeliverabilityKind } from '#/shared/api/generated/communication/models'

import { CampaignAudienceDialog } from './campaign-audience-dialog'
import { CampaignDeliverabilityDialog } from './campaign-deliverability-dialog'
import { CampaignDeliveryCard } from './campaign-delivery-card'
import { CampaignDetailRail } from './campaign-detail-rail'
import { CampaignEngagementCard } from './campaign-engagement-card'
import { CampaignPerformanceCard } from './campaign-performance-card'
import { CampaignReplyTimingCard } from './campaign-reply-timing-card'
import { CampaignStepPerformance } from './campaign-step-performance'
import { CampaignWaitingBandCard } from './campaign-waiting-band'
import type { CampaignDetailOverview } from '../model/use-campaign-detail'

interface CampaignOverviewTabProps {
  campaignId: string
  overview: CampaignDetailOverview
  onOpenWaiting: () => void
}

/**
 * The blocks the service marks null are meaningless for this campaign — nobody
 * waiting, a channel that never sent, too few bookings for an insight — so
 * they are absent rather than empty.
 */
export function CampaignOverviewTab({
  campaignId,
  overview,
  onOpenWaiting,
}: CampaignOverviewTabProps) {
  const [drillDown, setDrillDown] = useState<CampaignDeliverabilityKind | null>(
    null,
  )
  const [audienceOpen, setAudienceOpen] = useState(false)
  return (
    <div className="flex flex-col gap-4">
      {overview.waiting != null && (
        <CampaignWaitingBandCard
          band={overview.waiting}
          onOpenList={onOpenWaiting}
        />
      )}
      <CampaignPerformanceCard
        campaignId={campaignId}
        figures={overview.performance}
        basis={overview.performanceBasis}
      />
      <CampaignStepPerformance
        steps={overview.steps}
        insight={overview.stepInsight}
      />
      {/* Below the fold the page splits: the wide column keeps the message-level
          detail, the rail answers "who is where" at a glance. */}
      <div className="flex flex-col gap-4 xl:flex-row">
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          {overview.engagement.length > 0 && (
            <CampaignEngagementCard
              figures={overview.engagement}
              note={overview.engagementNote}
            />
          )}
          {overview.emailDelivery != null && (
            <CampaignDeliveryCard
              breakdown={overview.emailDelivery}
              onSeeAll={() => setDrillDown(CampaignDeliverabilityKind.bounced)}
            />
          )}
          {overview.smsDelivery != null && (
            <CampaignDeliveryCard
              breakdown={overview.smsDelivery}
              onSeeAll={() => setDrillDown(CampaignDeliverabilityKind.failed)}
            />
          )}
          {overview.replyBuckets.length > 0 && (
            <CampaignReplyTimingCard
              buckets={overview.replyBuckets}
              note={overview.replyNote}
            />
          )}
        </div>
        <div className="w-full shrink-0 xl:w-90">
          <CampaignDetailRail
            campaignId={campaignId}
            placement={overview.placement}
            placementNote={overview.placementNote}
            audience={overview.audience}
            cost={overview.cost}
            onEditAudience={() => setAudienceOpen(true)}
          />
        </div>
      </div>
      <CampaignDeliverabilityDialog
        campaignId={campaignId}
        kind={drillDown}
        onClose={() => setDrillDown(null)}
      />
      <CampaignAudienceDialog
        campaignId={campaignId}
        open={audienceOpen}
        onOpenChange={setAudienceOpen}
      />
    </div>
  )
}
